import { get } from 'lodash';

import createReducer from '../../helpers/CreateReducer';
import { ApiConstants } from '../../constants/actions';

export interface SettingsState {
  creditCardFee: number;
  voucherMinimumQuantity: number;
  voucherOrderMaxAmount: number;
  lunchMinAmount: number;
  lunchMaxAmount: number;
  lunchMidAmounts: number[];
  lunchAmountApplicableYear?: number;
  recreationalMinAmount: number;
  recreationalMaxAmount: number;
  recreationalMaxAmountForTopUpRequest?: number;
  massageMinAmount: number;
  massageMaxAmount: number;
  wellbeingMinAmount: number;
  wellbeingMaxAmount: number;
  transportMinAmount: number;
  transportMaxAmount: number;
  reCaptchaSiteKey?: string;
  onlinePaymentMinAmount?: number;
  offlinePaymentMinAmount?: number;
  companyBeneficiariesTotalAllowedForCompany?: number;
  isLoading: boolean;
  loaded: boolean;
  noPersistance?: boolean;
  isError?: boolean;
  feesVatPercentage: number;
  feePercentage: number;
  isLunchFundsLoadingTypePending: boolean;
  currentLunchFundsLoadingType?: string;
  anyDeferredCardOrders: boolean;
  maxWorkingDays: number;
}

interface SettingsPayload {
  creditCardFee?: number;
  voucherMinimumQuantity?: number;
  voucherOrderMaxAmount?: number;
  lunchMinAmount?: number;
  lunchMaxAmount?: number;
  lunchMidAmounts?: number[];
  lunchAmountApplicableYear?: number;
  recreationalMinAmount?: number;
  recreationalMaxAmount?: number;
  recreationalMaxAmountForTopUpRequest?: number;
  transportMinAmount?: number;
  transportMaxAmount?: number;
  massageMinAmount?: number;
  massageMaxAmount?: number;
  wellbeingMinAmount?: number;
  wellbeingMaxAmount?: number;
  reCaptchaSiteKey?: string;
  onlinePaymentMinAmount?: number;
  offlinePaymentMinAmount?: number;
  companyBeneficiariesTotalAllowedForCompany?: number;
  feesVatPercentage?: number;
}

export const getInitialState = (): SettingsState => ({
  creditCardFee: 0,
  voucherMinimumQuantity: 0,
  voucherOrderMaxAmount: 0,
  lunchMinAmount: 0,
  lunchMaxAmount: 0,
  lunchMidAmounts: [],
  recreationalMinAmount: 0,
  recreationalMaxAmount: 0,
  massageMinAmount: 0,
  massageMaxAmount: 0,
  wellbeingMinAmount: 1,
  wellbeingMaxAmount: 0,
  transportMinAmount: 0,
  transportMaxAmount: 0,
  isLoading: false,
  loaded: false,
  noPersistance: true,
  feesVatPercentage: 0,
  feePercentage: 0,
  isLunchFundsLoadingTypePending: false,
  currentLunchFundsLoadingType: undefined,
  anyDeferredCardOrders: false,
  maxWorkingDays: 0
});

export default createReducer(getInitialState, {
  [`${ApiConstants.LOAD_SETTINGS}_REQUEST`]: (): Partial<SettingsState> => ({
    isLoading: true
  }),

  [`${ApiConstants.LOAD_SETTINGS}_SUCCESS`]: (
    state: SettingsState,
    { payload }: { payload: SettingsPayload }
  ): Partial<SettingsState> => ({
    creditCardFee: get(payload, 'creditCardFee') || 0,
    voucherMinimumQuantity: get(payload, 'voucherMinimumQuantity') || 0,
    voucherOrderMaxAmount: get(payload, 'voucherOrderMaxAmount') || 0,
    lunchMinAmount: get(payload, 'lunchMinAmount') || 0,
    lunchMaxAmount: get(payload, 'lunchMaxAmount') || 0,
    lunchMidAmounts: get(payload, 'lunchMidAmounts') || [],
    lunchAmountApplicableYear:
      get(payload, 'lunchAmountApplicableYear') || new Date().getFullYear(),
    recreationalMinAmount: get(payload, 'recreationalMinAmount') || 0,
    recreationalMaxAmount: get(payload, 'recreationalMaxAmount') || 0,
    recreationalMaxAmountForTopUpRequest:
      get(payload, 'recreationalMaxAmountForTopUpRequest') || 0,
    transportMinAmount: get(payload, 'transportMinAmount') || 0,
    transportMaxAmount: get(payload, 'transportMaxAmount') || 0,
    massageMinAmount: get(payload, 'massageMinAmount') || 0,
    massageMaxAmount: get(payload, 'massageMaxAmount') || 0,
    wellbeingMinAmount: get(payload, 'wellbeingMinAmount') || 0,
    wellbeingMaxAmount: get(payload, 'wellbeingMaxAmount') || 0,
    reCaptchaSiteKey: get(payload, 'reCaptchaSiteKey') || '',
    onlinePaymentMinAmount: get(payload, 'onlinePaymentMinAmount') || 0,
    offlinePaymentMinAmount: get(payload, 'offlinePaymentMinAmount') || 0,
    companyBeneficiariesTotalAllowedForCompany:
      get(payload, 'companyBeneficiariesTotalAllowedForCompany') || 100,
    loaded: true,
    isLoading: false,
    feesVatPercentage: get(payload, 'feesVatPercentage') || 0
  }),

  [`${ApiConstants.LOAD_SETTINGS}_FAILURE`]: (
    state: SettingsState
  ): Partial<SettingsState> | SettingsState =>
    !state.isError
      ? {
          isLoading: false,
          loaded: true,
          isError: true
        }
      : state,

  [`${ApiConstants.GET_FEE}`]: (
    state: SettingsState,
    { feePercentage }: { feePercentage: number }
  ): Partial<SettingsState> => ({
    feePercentage
  }),

  [`${ApiConstants.GET_IS_LUNCH_FUNDS_LOADING_TYPE_PENDING}`]: (
    state: SettingsState,
    {
      isLunchFundsLoadingTypePending
    }: { isLunchFundsLoadingTypePending: boolean }
  ): Partial<SettingsState> => ({
    isLunchFundsLoadingTypePending
  }),

  [`${ApiConstants.GET_CURRENT_LUNCH_FUNDS_LOADING_TYPE}`]: (
    state: SettingsState,
    {
      currentLunchFundsLoadingType
    }: { currentLunchFundsLoadingType: string | undefined }
  ): Partial<SettingsState> => ({
    currentLunchFundsLoadingType
  }),

  [`${ApiConstants.GET_ANY_DEFERRED_CARD_ORDERS}`]: (
    state: SettingsState,
    { anyDeferredCardOrders }: { anyDeferredCardOrders: boolean }
  ): Partial<SettingsState> => ({
    anyDeferredCardOrders
  }),

  [`${ApiConstants.CLEAR_FEE}`]: (state: SettingsState): void => {
    state.feePercentage = 0;
  },

  [`${ApiConstants.GET_MAX_WORKING_DAYS}`]: (
    state: SettingsState,
    { maxWorkingDays }: { maxWorkingDays: number }
  ): Partial<SettingsState> => ({
    maxWorkingDays
  })
});
